import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Search, Filter, Eye, Send, FileCheck, Trash2, X,
  ChevronDown, AlertTriangle, RefreshCw, DollarSign, Clock, CheckCircle,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';
import { NEU } from '../neuStyles';

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KPICard({ icon: Icon, label, value, subtitle, color = '#2E8B98' }) {
  return (
    <div style={{
      background: '#F8F9FA', borderRadius: 12, padding: '20px 24px', border: 'none', boxShadow: NEU.shadow.sm,
      flex: '1 1 180px', minWidth: 160, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#212529', lineHeight: 1 }}>{value}</div>
      {subtitle && <span style={{ fontSize: 11, color: '#ADB5BD' }}>{subtitle}</span>}
    </div>
  );
}

// ─── Status Badge ──────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const statusConfig = {
    draft: { bg: '#f1f5f9', color: '#475569', label: 'Draft' },
    sent: { bg: '#dbeafe', color: '#1d4ed8', label: 'Sent' },
    accepted: { bg: '#dcfce7', color: '#15803d', label: 'Accepted' },
    rejected: { bg: '#fef2f2', color: '#DC3545', label: 'Rejected' },
    expired: { bg: '#fef3c7', color: '#b45309', label: 'Expired' },
    converted: { bg: '#e0e7ff', color: '#4338ca', label: 'Converted' },
  };
  const config = statusConfig[status] || statusConfig.draft;
  return (
    <span style={{
      background: config.bg, color: config.color, padding: '4px 12px',
      borderRadius: 6, fontSize: 12, fontWeight: 500, boxShadow: NEU.shadow.pill,
    }}>
      {config.label}
    </span>
  );
}

// ─── Create/Edit Estimate Modal ─────────────────────────────────────────────

function CreateEstimateModal({ isOpen, onClose, patients, onSave, initialData = null }) {
  const [formData, setFormData] = useState({
    patientId: initialData?.patientId || '',
    title: initialData?.title || '',
    description: initialData?.description || '',
    lineItems: initialData?.lineItems || [{ description: '', cptCode: '', icd10: '', hcpcs: '', quantity: 1, unitPrice: 0 }],
    taxRate: initialData?.taxRate || 0,
    discountAmount: initialData?.discountAmount || 0,
    validUntil: initialData?.validUntil || '',
    notes: initialData?.notes || '',
  });

  const subtotal = formData.lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
  const taxAmount = subtotal * (formData.taxRate / 100);
  const total = subtotal + taxAmount - (formData.discountAmount || 0);

  const handleLineItemChange = (index, field, value) => {
    const updated = [...formData.lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, lineItems: updated });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [...formData.lineItems, { description: '', cptCode: '', icd10: '', hcpcs: '', quantity: 1, unitPrice: 0 }],
    });
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      lineItems: formData.lineItems.filter((_, i) => i !== index),
    });
  };

  const handleSave = (status) => {
    if (!formData.patientId || !formData.title || formData.lineItems.length === 0) {
      alert('Please fill in patient, title, and add at least one line item');
      return;
    }
    onSave({
      ...formData,
      status: status || 'draft',
      subtotal,
      taxAmount,
      total,
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.lg,
        maxWidth: 900, width: '90%', maxHeight: '90vh', overflowY: 'auto', padding: 32,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#212529' }}>
            {initialData ? 'Edit Estimate' : 'Create New Estimate'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <X size={24} color="#6C757D" />
          </button>
        </div>

        {/* Patient & Title */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Patient *</label>
            <PatientDropdown
              patients={Array.isArray(patients) ? patients.map(p => ({
                ...p,
                first_name: p.first_name || p.firstName,
                last_name: p.last_name || p.lastName,
              })) : []}
              value={formData.patientId}
              onChange={val => setFormData({ ...formData, patientId: val })}
              placeholder="Select patient..."
              idPrefix="est"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Orthopedic Procedure Estimate"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
                fontSize: 13, color: '#212529', boxSizing: 'border-box', background: '#F8F9FA',
              }}
            />
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Description</label>
          <textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Additional details about this estimate..."
            rows="3"
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
              fontSize: 13, color: '#212529', boxSizing: 'border-box', fontFamily: 'inherit', background: '#F8F9FA',
            }}
          />
        </div>

        {/* Line Items */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>Line Items *</label>
            <button
              onClick={addLineItem}
              style={{
                background: 'none', border: 'none', color: '#2E8B98', borderRadius: 6, boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                padding: '4px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <Plus size={14} /> Add Item
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', background: '#F8F9FA' }}>
                  <th style={{ padding: 8, textAlign: 'left', color: '#6C757D', fontWeight: 600 }}>Description</th>
                  <th style={{ padding: 8, textAlign: 'left', color: '#6C757D', fontWeight: 600 }}>CPT</th>
                  <th style={{ padding: 8, textAlign: 'left', color: '#6C757D', fontWeight: 600 }}>ICD-10</th>
                  <th style={{ padding: 8, textAlign: 'left', color: '#6C757D', fontWeight: 600 }}>HCPCS</th>
                  <th style={{ padding: 8, textAlign: 'right', color: '#6C757D', fontWeight: 600 }}>Qty</th>
                  <th style={{ padding: 8, textAlign: 'right', color: '#6C757D', fontWeight: 600 }}>Unit Price</th>
                  <th style={{ padding: 8, textAlign: 'right', color: '#6C757D', fontWeight: 600 }}>Amount</th>
                  <th style={{ padding: 8, textAlign: 'center', color: '#6C757D', fontWeight: 600 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {formData.lineItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
                    <td style={{ padding: 8 }}>
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => handleLineItemChange(idx, 'description', e.target.value)}
                        placeholder="Service description"
                        style={{ width: '100%', padding: '6px', borderRadius: 4, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: '#F8F9FA' }}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <input
                        type="text"
                        value={item.cptCode}
                        onChange={e => handleLineItemChange(idx, 'cptCode', e.target.value)}
                        placeholder="CPT"
                        style={{ width: '100%', padding: '6px', borderRadius: 4, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: '#F8F9FA' }}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <input
                        type="text"
                        value={item.icd10}
                        onChange={e => handleLineItemChange(idx, 'icd10', e.target.value)}
                        placeholder="ICD-10"
                        style={{ width: '100%', padding: '6px', borderRadius: 4, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: '#F8F9FA' }}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <input
                        type="text"
                        value={item.hcpcs}
                        onChange={e => handleLineItemChange(idx, 'hcpcs', e.target.value)}
                        placeholder="HCPCS"
                        style={{ width: '100%', padding: '6px', borderRadius: 4, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: '#F8F9FA' }}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={e => handleLineItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        min="0"
                        style={{ width: '100%', padding: '6px', borderRadius: 4, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, textAlign: 'right', background: '#F8F9FA' }}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={e => handleLineItemChange(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        style={{ width: '100%', padding: '6px', borderRadius: 4, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, textAlign: 'right', background: '#F8F9FA' }}
                      />
                    </td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#212529', fontWeight: 500 }}>
                      ${((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <button
                        onClick={() => removeLineItem(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC3545', padding: 0 }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Subtotal</label>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>${subtotal.toFixed(2)}</div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Tax Rate (%)</label>
            <input
              type="number"
              value={formData.taxRate}
              onChange={e => setFormData({ ...formData, taxRate: parseFloat(e.target.value) || 0 })}
              min="0"
              max="100"
              step="0.01"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
                fontSize: 13, color: '#212529', boxSizing: 'border-box', background: '#F8F9FA',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Tax Amount</label>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>${taxAmount.toFixed(2)}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Discount Amount</label>
            <input
              type="number"
              value={formData.discountAmount}
              onChange={e => setFormData({ ...formData, discountAmount: parseFloat(e.target.value) || 0 })}
              min="0"
              step="0.01"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
                fontSize: 13, color: '#212529', boxSizing: 'border-box', background: '#F8F9FA',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Total</label>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#2E8B98' }}>${total.toFixed(2)}</div>
          </div>
        </div>

        {/* Valid Until & Notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Valid Until</label>
            <input
              type="date"
              value={formData.validUntil}
              onChange={e => setFormData({ ...formData, validUntil: e.target.value })}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
                fontSize: 13, color: '#212529', boxSizing: 'border-box', background: '#F8F9FA',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>Notes</label>
          <textarea
            value={formData.notes}
            onChange={e => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Internal notes..."
            rows="2"
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
              fontSize: 13, color: '#212529', boxSizing: 'border-box', fontFamily: 'inherit', background: '#F8F9FA',
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', borderRadius: 6, border: 'none', background: '#F8F9FA', boxShadow: NEU.shadow.sm,
              cursor: 'pointer', fontSize: 13, color: '#212529', fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave('draft')}
            style={{
              padding: '10px 20px', borderRadius: 6, border: 'none', background: '#2E8B98', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 500,
            }}
          >
            Save as Draft
          </button>
          <button
            onClick={() => handleSave('sent')}
            style={{
              padding: '10px 20px', borderRadius: 6, border: 'none', background: '#62BB46', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 500,
            }}
          >
            Send to Patient
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── View Estimate Drawer ──────────────────────────────────────────────────

function ViewEstimateDrawer({ isOpen, onClose, estimate, onConvert, onStatusChange }) {
  if (!isOpen || !estimate) return null;

  const lineItems = Array.isArray(estimate.lineItems) ? estimate.lineItems : [];
  const subtotal = lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
  const taxAmount = subtotal * (estimate.taxRate / 100);
  const total = subtotal + taxAmount - (estimate.discountAmount || 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'flex-end', zIndex: 999,
    }}>
      <div style={{
        background: '#F8F9FA', width: '100%', maxWidth: 600, height: '100vh', overflowY: 'auto',
        padding: 32, display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#212529' }}>Estimate Details</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <X size={24} color="#6C757D" />
          </button>
        </div>

        {/* Header Info */}
        <div style={{
          background: '#F8F9FA', borderRadius: 12, padding: 16, marginBottom: 20,
          border: 'none', boxShadow: NEU.shadow.sm,
        }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 600 }}>ESTIMATE ID</span>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>{estimate.id}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 600 }}>PATIENT</span>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>
              {estimate.patient?.firstName} {estimate.patient?.lastName}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 600 }}>TITLE</span>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>{estimate.title}</div>
          </div>
          <div>
            <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 600 }}>STATUS</span>
            <div style={{ marginTop: 4 }}>
              <StatusBadge status={estimate.status} />
            </div>
          </div>
        </div>

        {/* Description */}
        {estimate.description && (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 8 }}>Description</h4>
            <p style={{ margin: 0, fontSize: 13, color: '#6C757D', lineHeight: 1.5 }}>{estimate.description}</p>
          </div>
        )}

        {/* Line Items Table */}
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 12 }}>Line Items</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', background: '#F8F9FA' }}>
                  <th style={{ padding: 8, textAlign: 'left', color: '#6C757D', fontWeight: 600 }}>Description</th>
                  <th style={{ padding: 8, textAlign: 'right', color: '#6C757D', fontWeight: 600 }}>Qty</th>
                  <th style={{ padding: 8, textAlign: 'right', color: '#6C757D', fontWeight: 600 }}>Unit Price</th>
                  <th style={{ padding: 8, textAlign: 'right', color: '#6C757D', fontWeight: 600 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
                    <td style={{ padding: 8, color: '#212529' }}>{item.description}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#212529' }}>{item.quantity}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#212529' }}>${(item.unitPrice || 0).toFixed(2)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#212529', fontWeight: 500 }}>
                      ${((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div style={{
          background: '#F8F9FA', borderRadius: 12, padding: 16, marginBottom: 20,
          border: 'none', boxShadow: NEU.shadow.sm,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: '#6C757D' }}>Subtotal:</span>
            <span style={{ fontSize: 12, color: '#212529', textAlign: 'right', fontWeight: 500 }}>${subtotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: '#6C757D' }}>Tax ({estimate.taxRate}%):</span>
            <span style={{ fontSize: 12, color: '#212529', textAlign: 'right', fontWeight: 500 }}>${taxAmount.toFixed(2)}</span>
          </div>
          {estimate.discountAmount > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: '#6C757D' }}>Discount:</span>
              <span style={{ fontSize: 12, color: '#212529', textAlign: 'right', fontWeight: 500 }}>-${(estimate.discountAmount || 0).toFixed(2)}</span>
            </div>
          )}
          <div style={{ borderTop: 'none', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.45)', paddingTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>Total:</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#2E8B98', textAlign: 'right' }}>${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Notes */}
        {estimate.notes && (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 8 }}>Notes</h4>
            <p style={{ margin: 0, fontSize: 13, color: '#6C757D', lineHeight: 1.5 }}>{estimate.notes}</p>
          </div>
        )}

        {/* Valid Until */}
        <div style={{ marginBottom: 24 }}>
          <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 600 }}>VALID UNTIL</span>
          <div style={{ fontSize: 13, color: '#212529', marginTop: 4 }}>
            {estimate.validUntil ? new Date(estimate.validUntil).toLocaleDateString() : 'N/A'}
          </div>
        </div>

        {/* Converted Invoice Link */}
        {estimate.status === 'converted' && estimate.invoiceId && (
          <div style={{
            background: '#dcfce7', border: 'none', borderRadius: 8, padding: 12, boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
            marginBottom: 20, fontSize: 13, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <CheckCircle size={16} />
            Converted to Invoice #{estimate.invoiceId}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
          {estimate.status !== 'converted' && (
            <>
              <button
                onClick={() => onConvert(estimate.id)}
                style={{
                  padding: '12px 16px', borderRadius: 6, border: 'none', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                  background: '#4338ca', cursor: 'pointer', fontSize: 13, color: '#fff',
                  fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <FileCheck size={16} /> Convert to Invoice
              </button>
              {estimate.status !== 'sent' && (
                <button
                  onClick={() => onStatusChange(estimate.id, 'sent')}
                  style={{
                    padding: '12px 16px', borderRadius: 6, border: 'none', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                    background: '#2E8B98', cursor: 'pointer', fontSize: 13, color: '#fff',
                    fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <Send size={16} /> Send to Patient
                </button>
              )}
              {estimate.status !== 'accepted' && (
                <button
                  onClick={() => onStatusChange(estimate.id, 'accepted')}
                  style={{
                    padding: '12px 16px', borderRadius: 6, border: 'none', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                    background: '#62BB46', cursor: 'pointer', fontSize: 13, color: '#fff',
                    fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <CheckCircle size={16} /> Mark Accepted
                </button>
              )}
              <button
                onClick={() => onStatusChange(estimate.id, 'rejected')}
                style={{
                  padding: '12px 16px', borderRadius: 6, border: 'none', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                  background: '#DC3545', cursor: 'pointer', fontSize: 13, color: '#fff',
                  fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <X size={16} /> Mark Rejected
              </button>
            </>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '12px 16px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.sm,
              background: '#F8F9FA', cursor: 'pointer', fontSize: 13, color: '#212529',
              fontWeight: 500,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

function BillingEstimatesPage({ user, onNavigate }) {
  const [estimates, setEstimates] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [estimatesData, patientsData] = await Promise.all([
        window.zignagenixAPI.getBillingEstimates({ status: statusFilter !== 'all' ? statusFilter : null }),
        window.zignagenixAPI.getPatients(),
      ]);

      if (estimatesData?.error) throw new Error(estimatesData.message);
      setEstimates(Array.isArray(estimatesData) ? estimatesData : []);
      setPatients(Array.isArray(patientsData) ? patientsData : []);
    } catch (err) {
      setError(err.message || 'Failed to load billing estimates');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateEstimate = async (data) => {
    try {
      const result = await window.zignagenixAPI.createBillingEstimate(data);
      if (result?.error) throw new Error(result.message);
      setShowCreateModal(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create estimate');
    }
  };

  const handleConvert = async (estimateId) => {
    try {
      const result = await window.zignagenixAPI.convertEstimateToInvoice(estimateId);
      if (result?.error) throw new Error(result.message);
      if (!result.success) throw new Error('Failed to convert estimate');
      alert(`Invoice #${result.invoiceId} created successfully`);
      setSelectedEstimate(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to convert estimate');
    }
  };

  const handleStatusChange = async (estimateId, newStatus) => {
    try {
      const result = await window.zignagenixAPI.updateBillingEstimateStatus(estimateId, newStatus);
      if (result?.error) throw new Error(result.message);
      setSelectedEstimate(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleDelete = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    try {
      const result = await window.zignagenixAPI.deactivateBillingEstimate(estimateId);
      if (result?.error) throw new Error(result.message);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete estimate');
    }
  };

  const filteredEstimates = estimates.filter(e => {
    const searchLower = searchTerm.toLowerCase();
    const patientName = `${e.patient?.firstName || ''} ${e.patient?.lastName || ''}`.toLowerCase();
    return patientName.includes(searchLower) || (e.title || '').toLowerCase().includes(searchLower) || (e.id || '').toLowerCase().includes(searchLower);
  });

  const stats = {
    total: estimates.length,
    draft: estimates.filter(e => e.status === 'draft').length,
    sent: estimates.filter(e => e.status === 'sent').length,
    accepted: estimates.filter(e => e.status === 'accepted').length,
    converted: estimates.filter(e => e.status === 'converted').length,
  };

  if (loading && estimates.length === 0) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: '#6C757D' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <p>Loading billing estimates...</p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: 24, overflowY: 'auto', background: '#F8F9FA', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #2E8B98, #256F7A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#212529' }}>Billing Estimates</h1>
            <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>Create and manage patient cost estimates</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PrintButton pageTitle="Billing Estimates" subtitle="Patient Cost Estimates" />
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none', background: '#2E8B98', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Plus size={16} /> Create Estimate
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: 'none', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#DC3545', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KPICard icon={FileText} label="Total Estimates" value={stats.total} color="#2E8B98" />
        <KPICard icon={Clock} label="Draft" value={stats.draft} color="#6C757D" />
        <KPICard icon={Send} label="Sent" value={stats.sent} color="#1d4ed8" />
        <KPICard icon={CheckCircle} label="Accepted" value={stats.accepted} color="#62BB46" />
        <KPICard icon={FileCheck} label="Converted" value={stats.converted} color="#4338ca" />
      </div>

      {/* Filter Bar */}
      <div style={{
        background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, padding: 16,
        marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <Filter size={16} color="#6C757D" />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: '6px 12px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12,
            color: '#212529', cursor: 'pointer', background: '#F8F9FA',
          }}
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
          <option value="converted">Converted</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flex: 1, maxWidth: 300 }}>
          <Search size={14} color="#6C757D" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search patient, title, or ID..."
            style={{
              flex: 1, padding: '6px 8px', borderRadius: 6, border: 'none', boxShadow: NEU.shadow.insetSm,
              fontSize: 12, color: '#212529', boxSizing: 'border-box', background: '#F8F9FA',
            }}
          />
        </div>
      </div>

      {/* Estimates Table */}
      <div style={{
        background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, overflow: 'hidden',
      }}>
        {filteredEstimates.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#ADB5BD', fontSize: 13 }}>
            {estimates.length === 0 ? 'No billing estimates yet. Create one to get started.' : 'No results match your search.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', background: '#F8F9FA' }}>
                  <th style={{ padding: 12, textAlign: 'left', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>ID</th>
                  <th style={{ padding: 12, textAlign: 'left', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Patient</th>
                  <th style={{ padding: 12, textAlign: 'left', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Title</th>
                  <th style={{ padding: 12, textAlign: 'center', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Line Items</th>
                  <th style={{ padding: 12, textAlign: 'right', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Total Amount</th>
                  <th style={{ padding: 12, textAlign: 'center', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'center', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Valid Until</th>
                  <th style={{ padding: 12, textAlign: 'center', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Created</th>
                  <th style={{ padding: 12, textAlign: 'center', color: '#6C757D', fontWeight: 600, fontSize: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEstimates.map((est, idx) => {
                  const lineItems = Array.isArray(est.lineItems) ? est.lineItems : [];
                  const total = lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0) + (est.taxAmount || 0) - (est.discountAmount || 0);
                  const createdDate = est.createdAt ? new Date(est.createdAt).toLocaleDateString() : 'N/A';
                  const validUntilDate = est.validUntil ? new Date(est.validUntil).toLocaleDateString() : 'N/A';

                  return (
                    <tr key={idx} style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', color: '#212529' }}>
                      <td style={{ padding: 12, fontSize: 12 }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{est.id}</span>
                      </td>
                      <td style={{ padding: 12, fontSize: 12 }}>
                        {est.patient?.firstName} {est.patient?.lastName}
                      </td>
                      <td style={{ padding: 12, fontSize: 12 }}>{est.title}</td>
                      <td style={{ padding: 12, textAlign: 'center', fontSize: 12 }}>{lineItems.length}</td>
                      <td style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 500 }}>
                        ${total.toFixed(2)}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <StatusBadge status={est.status} />
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', fontSize: 12 }}>{validUntilDate}</td>
                      <td style={{ padding: 12, textAlign: 'center', fontSize: 12 }}>{createdDate}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                          <button
                            onClick={() => setSelectedEstimate(est)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', color: '#2E8B98',
                              padding: 0, fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 2,
                            }}
                          >
                            <Eye size={14} /> View
                          </button>
                          {est.status !== 'converted' && (
                            <button
                              onClick={() => handleDelete(est.id)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', color: '#DC3545',
                                padding: 0, fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 2,
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateEstimateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        patients={patients}
        onSave={handleCreateEstimate}
      />
      <ViewEstimateDrawer
        isOpen={!!selectedEstimate}
        onClose={() => setSelectedEstimate(null)}
        estimate={selectedEstimate}
        onConvert={handleConvert}
        onStatusChange={handleStatusChange}
      />

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default BillingEstimatesPage;
